/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, Box, TextField, Stack, styled } from '@mui/material';

import { ICustomDropDownProps } from '../Graphs/Graphs.Model';
import { itemsPerPage } from '../../../Utilities/utils';

import { Icons } from '../../../Constants/Icons';
import theme from '../../../Themes/theme';

import { dropDownStyle, FilterField } from '../GraphContainer/GraphContainer.styles';
import { JobsTitle } from '../../../../Modules/Jobs/Components/IssuesRaised/JobsIssuesRaised.styles';
import { StyledPagination } from '../CommonStyle/Pagination.Style';
import { StyleConstants } from '../../../Constants/Style.Constants';
import { styles } from '../../../../Modules/Vendor/Components/AllSchedules/ModifyServices/ModifyServices.Style';
import { DropdownWrapper } from './CustomDropdown.style';

const CustomDropDownAutoComplete: React.FC<ICustomDropDownProps> = ({
  options,
  selectedValue,
  onChange,
  isLoading,
  title,
  totalCounts,
  handlePaginationChanges,
  currentPages,
  searchString = '',
  setSearchString,
  onSearch,
  dropDownWidth,
  dropDownHeight,
  isInline,
  isDisabled,
  noOptionsText,
  label,
  sx,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const mousedownInsideListboxRef = useRef(false);
  const { dropdown } = dropDownStyle;
  const { dropdownStyles } = styles;

  const { t } = useTranslation(['assignJob', 'oneTimeJob']);

  const CustomListbox = styled('ul')({
    borderRadius: '16px',
    padding: 0,
    margin: 0,
    position: 'relative',
  });

  const handleClearSearch = () => {
    if (isDisabled) return;
    setSearchString('');
    onChange({ target: { value: null } });
  };

  return (
    <FilterField>
      {!isInline && <JobsTitle sx={{ fontSize: theme.typography.h4?.fontSize }}>{title}</JobsTitle>}
      <Box sx={{ width: dropDownWidth ? dropDownWidth : 'auto' }}>
        <Autocomplete
          options={options}
          getOptionLabel={(option) => option.label || ''}
          value={options.find((opt) => opt.value === selectedValue) || null}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={(event, reason) => {
            if (reason === 'blur' && mousedownInsideListboxRef.current) return;
            setIsOpen(false);
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              onChange({ target: { value: newValue.value } });
            } else {
              onChange({ target: { value: null } });
            }
          }}
          disabled={isDisabled}
          clearOnBlur={false}
          loading={isLoading}
          {...(noOptionsText ? { noOptionsText } : {})}
          loadingText={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
              }}>
              <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
            </Box>
          }
          inputValue={searchString}
          onInputChange={(event, newInputValue, reason) => {
            setSearchString(newInputValue);
            if (reason === 'input' && onSearch) {
              onSearch(newInputValue);
            }
          }}
          ListboxProps={{
            sx: {
              maxHeight: dropDownHeight ? dropDownHeight : 600,
              padding: 0,
              margin: 0,
              overflowY: 'auto',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            },
          }}
          componentsProps={{
            paper: {
              sx: {
                borderRadius: '1rem',
                overflow: 'hidden',
              },
            },
          }}
          ListboxComponent={(props) => (
            <CustomListbox
              onMouseDown={() => {
                mousedownInsideListboxRef.current = true;
              }}
              onMouseUp={() => {
                mousedownInsideListboxRef.current = false;
              }}>
              <ul
                {...props}
                style={{
                  maxHeight: dropDownHeight ? dropDownHeight : 300,
                  overflowY: 'auto',
                  padding: 0,
                  margin: 0,
                }}>
                {isLoading && (
                  <DropdownWrapper>
                    <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                  </DropdownWrapper>
                )}
                {props.children}
              </ul>
              {totalCounts > 10 && (
                <Stack spacing={2} sx={{ padding: '0.5rem', borderTop: `1px solid ${theme.palette.divider}` }}>
                  <StyledPagination
                    count={Math.ceil(totalCounts / itemsPerPage)}
                    page={currentPages}
                    onChange={handlePaginationChanges}
                    color="primary"
                  />
                </Stack>
              )}
            </CustomListbox>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                label ? (
                  <span>
                    {label}
                    <span className="mandatory-field"> *</span>
                  </span>
                ) : (
                  ''
                )
              }
              placeholder={label ? undefined : t('oneTimeJob:search')}
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              sx={{
                ...(isInline ? dropdownStyles : dropdown),
                ...sx,
                opacity: isDisabled ? 0.5 : 1,
                pointerEvents: isLoading ? 'none' : 'all',

                '& .MuiInputBase-input::placeholder': {
                  color: isInline ? theme.palette.common.black : theme.palette.primary.dark,
                  fontSize: isInline ? theme.typography.h4?.fontSize : theme.typography.h5?.fontSize,
                  fontWeight: isInline ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: isLoading ? (
                  <CircularProgress size={20} sx={{ color: theme.palette.primary.dark }} />
                ) : (
                  <>
                    {searchString ? (
                      <img
                        src={Icons.CancelIcon}
                        alt="clear"
                        onClick={handleClearSearch}
                        style={{
                          height: '1.25rem',
                          width: '1.25rem',
                          filter:
                            'invert(26%) sepia(57%) saturate(546%) hue-rotate(130deg) brightness(90%) contrast(92%)',
                          opacity: isDisabled ? '0.5' : '1',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                      />
                    ) : (
                      <img
                        src={Icons.SearchIcon}
                        alt="search"
                        style={{
                          height: '1.25rem',
                          width: '1.25rem',
                          filter:
                            'invert(26%) sepia(57%) saturate(546%) hue-rotate(130deg) brightness(90%) contrast(92%)',
                          opacity: isDisabled ? '0.5' : '1',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                      />
                    )}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option, { selected }) => {
            const isSelected = option.value === (selectedValue ?? selectedValue);

            return (
              <li
                {...props}
                style={{
                  padding: '12px',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.h3?.fontSize,
                  fontWeight: isSelected ? theme.typography.fontWeightBold : theme.typography.fontWeightLight,
                  lineHeight: '1.375rem',
                  letterSpacing: StyleConstants.LetterSpacingTight,
                  backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
                  borderBottom: `0.0625rem solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.palette.secondary.dark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selected ? theme.palette.primary.main : 'transparent';
                }}>
                {option.label}
              </li>
            );
          }}
          popupIcon={null}
          clearIcon={null}
        />
      </Box>
    </FilterField>
  );
};

export default CustomDropDownAutoComplete;
